import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';

export default function LoginPage() {
  const [form, setForm] = useState({ emailOrPhone: '', password: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verifEmail, setVerifEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendDone, setResendDone] = useState(false);
  const { login, isLoading } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.emailOrPhone || !form.password) {
      toast.error('Remplissez tous les champs.');
      return;
    }
    const result = await login(form.emailOrPhone, form.password);
    if (result.success) {
      toast.success('Bienvenue !');
      navigate(result.user.role === 'CLIENT' ? '/dashboard' : '/espace-prestataire');
    } else {
      // Vérification email requise → afficher le panneau de renvoi
      if (result.requiresEmailVerification) {
        setVerifEmail(result.email || form.emailOrPhone);
        setNeedsVerification(true);
      } else {
        toast.error(result.error);
      }
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await api.post('/auth/resend-verification', { email: verifEmail });
      setResendDone(true);
      toast.success('Email de vérification renvoyé !');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de l\'envoi.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', paddingTop: '80px',
      background: 'linear-gradient(135deg, #0D1B2A 0%, #1A3B5D 60%, #0D1B2A 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '2rem', position: 'relative', overflow: 'hidden',
    }}>
      {/* Gears décoratifs */}
      {[
        { size: 380, top: '-12%', left: '-8%', speed: 30 },
        { size: 180, bottom: '-6%', right: '-4%', speed: -18 },
      ].map((g, i) => (
        <motion.div key={i}
          animate={{ rotate: g.speed > 0 ? 360 : -360 }}
          transition={{ duration: Math.abs(g.speed), repeat: Infinity, ease: 'linear' }}
          style={{
            position: 'absolute', fontSize: g.size, lineHeight: 1,
            top: g.top, bottom: g.bottom, left: g.left, right: g.right,
            color: 'rgba(255,107,0,0.04)', pointerEvents: 'none',
          }}
        >⚙</motion.div>
      ))}

      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5 }}
        style={{
          background: 'white', borderRadius: '20px',
          padding: 'clamp(2rem, 5vw, 3.5rem)',
          width: '100%', maxWidth: '460px',
          boxShadow: '0 40px 100px rgba(0,0,0,0.5)',
          position: 'relative', zIndex: 1,
        }}
      >
        <AnimatePresence mode="wait">

          {/* ── PANNEAU : EMAIL NON VÉRIFIÉ ── */}
          {needsVerification ? (
            <motion.div key="verify"
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}
            >
              <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                <div style={{ fontSize: '3.5rem', marginBottom: '1rem' }}>📧</div>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', color: 'var(--blue-dark)', marginBottom: '0.5rem' }}>
                  VÉRIFIEZ<br /><span style={{ color: 'var(--orange)' }}>VOTRE EMAIL</span>
                </h2>
                <p style={{ color: 'var(--gray-text)', fontSize: '0.9rem', lineHeight: 1.7 }}>
                  Votre compte n'est pas encore activé. Un lien de confirmation a été envoyé à :
                </p>
                <div style={{
                  background: 'var(--gray-light)', border: '2px solid rgba(255,107,0,0.2)',
                  borderRadius: '10px', padding: '0.7rem 1rem', margin: '0.8rem 0',
                  fontFamily: 'var(--font-cond)', fontWeight: 700, color: 'var(--blue-dark)',
                }}>
                  {verifEmail}
                </div>
                <p style={{ fontSize: '0.8rem', color: 'rgba(0,0,0,0.38)' }}>
                  Vérifiez aussi votre dossier <strong>Spam</strong>.
                </p>
              </div>

              {!resendDone ? (
                <button
                  onClick={handleResend}
                  disabled={resendLoading}
                  className="btn btn-primary btn-full"
                  style={{ marginBottom: '1rem' }}
                >
                  {resendLoading ? <><span className="spinner" /> Envoi...</> : '📧 RENVOYER LE LIEN DE VÉRIFICATION'}
                </button>
              ) : (
                <div style={{
                  background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.25)',
                  borderRadius: '10px', padding: '1rem', textAlign: 'center', marginBottom: '1rem',
                }}>
                  <p style={{ color: '#065f46', fontWeight: 600 }}>✅ Email renvoyé ! Vérifiez votre boîte mail.</p>
                </div>
              )}

              <button
                onClick={() => { setNeedsVerification(false); setResendDone(false); }}
                style={{ background: 'none', border: 'none', color: 'var(--gray-text)', fontSize: '0.85rem', cursor: 'pointer', width: '100%', textAlign: 'center', textDecoration: 'underline' }}
              >
                ← Retour à la connexion
              </button>
            </motion.div>

          ) : (

          /* ── FORMULAIRE DE CONNEXION NORMAL ── */
          <motion.div key="login"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
          >
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
                style={{ fontSize: '3rem', display: 'inline-block', marginBottom: '0.5rem' }}>⚙️</motion.div>
              <h1 style={{
                fontFamily: 'var(--font-display)', fontSize: '2.8rem',
                color: 'var(--blue-dark)', letterSpacing: '2px', lineHeight: 1, margin: '0.3rem 0',
              }}>
                BON<br /><span style={{ color: 'var(--orange)' }}>RETOUR</span>
              </h1>
              <p style={{ color: 'var(--gray-text)', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                Connectez-vous à votre compte ELIMdépannage
              </p>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
              <div className="form-group">
                <label className="form-label">Email ou téléphone</label>
                <input
                  className="form-input"
                  value={form.emailOrPhone}
                  onChange={e => setForm(p => ({ ...p, emailOrPhone: e.target.value }))}
                  placeholder="votre@email.com ou +229 XX XX XX XX"
                  autoComplete="username"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Mot de passe</label>
                <div style={{ position: 'relative' }}>
                  <input
                    className="form-input"
                    type={showPwd ? 'text' : 'password'}
                    value={form.password}
                    onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    style={{ paddingRight: '3rem' }}
                  />
                  <button type="button" onClick={() => setShowPwd(!showPwd)} style={{
                    position: 'absolute', right: '0.8rem', top: '50%',
                    transform: 'translateY(-50%)', background: 'none',
                    border: 'none', cursor: 'pointer', color: 'var(--gray-text)',
                    display: 'flex', alignItems: 'center',
                  }}>
                    {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Link to="/mot-de-passe-oublie" style={{ fontSize: '0.82rem', color: 'var(--orange)' }}>
                    Mot de passe oublié ?
                  </Link>
                </div>
              </div>

              <button type="submit" disabled={isLoading} className="btn btn-primary btn-full btn-lg">
                {isLoading ? <><span className="spinner" /> CONNEXION...</> : 'SE CONNECTER →'}
              </button>
            </form>

            <div className="divider" style={{ margin: '1.8rem 0 1.2rem' }}>Pas encore inscrit ?</div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
              <Link to="/inscription?type=client" className="btn btn-secondary btn-sm" style={{ textAlign: 'center' }}>
                🚗 Je suis client
              </Link>
              <Link to="/inscription?type=prestataire" className="btn btn-primary btn-sm" style={{ textAlign: 'center' }}>
                🔧 Je suis mécanicien
              </Link>
            </div>
          </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
